import type { FC } from 'react'

import { Input, Select, Skeleton } from '@skytech/ui'
import { ALLOWED_WHITE_LABELS_FOR_DEALER, getWhiteLabelForDealerStyle } from '@skytech/utils'

import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { useSearchParamsByKey } from '@/hooks/searchParams'

import { ResultCount } from '../ResultCount'

import { DEFAULT_ORDER_TYPE, TERMINATION_ORDER_TYPE } from './consts'
import { ExportToExcelOrders } from './ExportToExcelOrders'
import type { OrderStatusCount } from './resources'
import { StatusSelect } from './StatusSelect'
import { StatusTabs } from './StatusTabs'

export const SubscriptionOrderFilters: FC<{
  textSearch: string
  status: string
  statusCounter: OrderStatusCount
  totalCount?: number
  isTotalCountLoading?: boolean
  setTextSearch: (val: string) => void
}> = ({ textSearch, status, statusCounter, totalCount, isTotalCountLoading, setTextSearch }) => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()
  const [queryOrderType] = useSearchParamsByKey('type')
  const [queryOrderTerminationType, setQueryOrderTerminationType] =
    useSearchParamsByKey('terminationType')
  const useUserWhiteLabel = ALLOWED_WHITE_LABELS_FOR_DEALER.includes(whiteLabel)

  const terminationOrderTypeOptions = [
    { value: '', label: translate('All termination types') },
    ...Object.entries(TERMINATION_ORDER_TYPE).map(([key, value]) => ({
      value: String(value),
      label: translate(`TERMINATION-ORDER-TYPE/${key.toLowerCase()}`),
    })),
  ]

  return (
    <>
      <StatusTabs statusCounter={statusCounter} />

      <StatusSelect statusCounter={statusCounter} />

      <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-2">
        <Input
          useDefaultWhiteLabel={!useUserWhiteLabel}
          id="subscription-orders-search"
          type="text"
          wrapperClassName="max-md:w-full"
          value={textSearch}
          placeholder={translate('Search')}
          onChange={e => setTextSearch(e.target.value)}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
        {queryOrderType === 'termination' && (
          <Select
            id="termination-type"
            className="order-first w-full md:order-last md:w-fit md:min-w-48"
            value={queryOrderTerminationType || ''}
            options={terminationOrderTypeOptions}
            onChange={v => {
              if (!v) return
              setQueryOrderTerminationType(v.value === '' ? '' : v.value.toString())
            }}
          />
        )}
      </div>

      <div className="flex flex-col items-start">
        {isTotalCountLoading ? (
          <Skeleton width={250} />
        ) : (
          <ExportToExcelOrders
            queryOrderType={queryOrderType || DEFAULT_ORDER_TYPE}
            querySearch={textSearch}
            status={status}
            count={totalCount}
          />
        )}

        {isTotalCountLoading ? (
          <Skeleton width={100} />
        ) : (
          totalCount && <ResultCount count={totalCount} />
        )}
      </div>
    </>
  )
}
